import { Goal, Payment } from '../context/GoalsContext';
import { Income } from '../types/income';

export interface GoalAnalytics {
  completionRate: number;
  averageCompletionTime: number;
  successRateByPriority: Record<string, number>;
  monthlyProgressTrend: Array<{ month: string; progress: number }>;
  typePerformance: Record<string, { count: number; successRate: number; avgTime: number }>;
}

export interface FinancialInsights {
  monthlyContributionTrend: Array<{ month: string; amount: number }>;
  incomeAllocationEfficiency: number;
  debtPayoffAcceleration: number;
  savingsRateOptimization: number;
  optimalContributions: Record<string, number>;
}

export interface PredictiveAnalytics {
  estimatedCompletionDates: Record<string, string>;
  goalAchievementProbability: Record<string, number>;
  riskAssessment: Record<string, 'low' | 'medium' | 'high'>;
  recommendations: string[];
}

export class AnalyticsService {
  static calculateGoalAnalytics(goals: Goal[]): GoalAnalytics {
    const completedGoals = goals.filter(goal => goal.currentAmount >= goal.targetAmount);
    const completionRate = goals.length > 0 ? (completedGoals.length / goals.length) * 100 : 0;

    // Calculate average completion time
    const completionTimes = completedGoals.map(goal => {
      const startDate = new Date(goal.createdAt || goal.lastUpdated);
      const endDate = new Date(goal.lastUpdated);
      return (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24); // days
    });
    const averageCompletionTime = completionTimes.length > 0 
      ? completionTimes.reduce((sum, time) => sum + time, 0) / completionTimes.length 
      : 0;

    // Success rate by priority
    const successRateByPriority: Record<string, number> = {};
    const priorityGroups = goals.reduce((groups, goal) => {
      if (!groups[goal.priority]) groups[goal.priority] = [];
      groups[goal.priority].push(goal);
      return groups;
    }, {} as Record<string, Goal[]>);

    Object.entries(priorityGroups).forEach(([priority, priorityGoals]) => {
      const completed = priorityGoals.filter(goal => goal.currentAmount >= goal.targetAmount);
      successRateByPriority[priority] = priorityGoals.length > 0 
        ? (completed.length / priorityGoals.length) * 100 
        : 0;
    });

    // Monthly progress trend (last 6 months)
    const monthlyProgressTrend = this.calculateMonthlyProgressTrend(goals);

    // Type performance
    const typePerformance: Record<string, { count: number; successRate: number; avgTime: number }> = {};
    const typeGroups = goals.reduce((groups, goal) => {
      if (!groups[goal.type]) groups[goal.type] = [];
      groups[goal.type].push(goal);
      return groups;
    }, {} as Record<string, Goal[]>);

    Object.entries(typeGroups).forEach(([type, typeGoals]) => {
      const completed = typeGoals.filter(goal => goal.currentAmount >= goal.targetAmount);
      const typeCompletionTimes = completed.map(goal => {
        const startDate = new Date(goal.createdAt || goal.lastUpdated);
        const endDate = new Date(goal.lastUpdated);
        return (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24);
      });
      
      typePerformance[type] = {
        count: typeGoals.length,
        successRate: typeGoals.length > 0 ? (completed.length / typeGoals.length) * 100 : 0,
        avgTime: typeCompletionTimes.length > 0 
          ? typeCompletionTimes.reduce((sum, time) => sum + time, 0) / typeCompletionTimes.length 
          : 0
      };
    });

    return {
      completionRate,
      averageCompletionTime,
      successRateByPriority,
      monthlyProgressTrend,
      typePerformance
    };
  }

  static calculateFinancialInsights(goals: Goal[], income: Income): FinancialInsights {
    // Monthly contribution trend
    const monthlyContributionTrend = this.calculateMonthlyContributionTrend(goals);

    // Income allocation efficiency
    const totalAllocated = goals.reduce((sum, goal) => sum + goal.monthlyContribution, 0);
    const incomeAllocationEfficiency = income.monthlyIncome > 0 
      ? (totalAllocated / income.monthlyIncome) * 100 
      : 0;

    // Debt payoff acceleration (how much faster than minimum payments)
    const debtGoals = goals.filter(goal => goal.type === 'Debt');
    const debtPayoffAcceleration = debtGoals.length > 0 
      ? debtGoals.reduce((sum, goal) => {
          const minPayment = goal.originalTargetAmount ? goal.originalTargetAmount * 0.02 : goal.monthlyContribution;
          return sum + (goal.monthlyContribution / minPayment);
        }, 0) / debtGoals.length 
      : 0;

    // Savings rate optimization
    const savingsGoals = goals.filter(goal => goal.type === 'Savings');
    const totalSavingsRate = savingsGoals.reduce((sum, goal) => sum + goal.monthlyContribution, 0);
    const savingsRateOptimization = income.monthlyIncome > 0 
      ? (totalSavingsRate / income.monthlyIncome) * 100 
      : 0;

    // Optimal contributions based on income and goal priorities
    const optimalContributions: Record<string, number> = {};
    goals.forEach(goal => {
      const priority = goal.priority;
      const remainingAmount = goal.targetAmount - goal.currentAmount;
      const monthsToDeadline = this.calculateMonthsToDeadline(goal.deadline);
      
      if (monthsToDeadline > 0) {
        const baseContribution = remainingAmount / monthsToDeadline;
        const priorityMultiplier = priority === 'Essential' ? 1.5 : priority === 'Important' ? 1.2 : 1.0;
        optimalContributions[goal.id] = baseContribution * priorityMultiplier;
      }
    });

    return {
      monthlyContributionTrend,
      incomeAllocationEfficiency,
      debtPayoffAcceleration,
      savingsRateOptimization,
      optimalContributions
    };
  }

  static calculatePredictiveAnalytics(goals: Goal[]): PredictiveAnalytics {
    const estimatedCompletionDates: Record<string, string> = {};
    const goalAchievementProbability: Record<string, number> = {};
    const riskAssessment: Record<string, 'low' | 'medium' | 'high'> = {};
    const recommendations: string[] = [];

    goals.forEach(goal => {
      // Check if goal is completed
      const isCompleted = goal.currentAmount >= goal.targetAmount;
      
      if (isCompleted) {
        // Completed goals are always low risk
        goalAchievementProbability[goal.id] = 100;
        riskAssessment[goal.id] = 'low';
        estimatedCompletionDates[goal.id] = 'Completed';
        return; // Skip further calculations for completed goals
      }

      // Calculate estimated completion date for incomplete goals
      const remainingAmount = goal.targetAmount - goal.currentAmount;
      if (goal.monthlyContribution > 0) {
        const monthsToComplete = remainingAmount / goal.monthlyContribution;
        const estimatedDate = new Date();
        estimatedDate.setMonth(estimatedDate.getMonth() + Math.ceil(monthsToComplete));
        estimatedCompletionDates[goal.id] = estimatedDate.toISOString().split('T')[0];
      }

      // Calculate achievement probability for incomplete goals
      const monthsToDeadline = this.calculateMonthsToDeadline(goal.deadline);
      const requiredMonthlyContribution = remainingAmount / monthsToDeadline;
      const currentContribution = goal.monthlyContribution;
      
      if (monthsToDeadline > 0 && requiredMonthlyContribution > 0) {
        const probability = Math.min((currentContribution / requiredMonthlyContribution) * 100, 100);
        goalAchievementProbability[goal.id] = Math.max(probability, 0);
      } else {
        goalAchievementProbability[goal.id] = 0;
      }

      // Risk assessment for incomplete goals
      const probability = goalAchievementProbability[goal.id];
      if (probability >= 80) {
        riskAssessment[goal.id] = 'low';
      } else if (probability >= 50) {
        riskAssessment[goal.id] = 'medium';
      } else {
        riskAssessment[goal.id] = 'high';
      }
    });

    // Generate recommendations (only for incomplete goals)
    const incompleteGoals = goals.filter(goal => goal.currentAmount < goal.targetAmount);
    const highRiskGoals = incompleteGoals.filter(goal => riskAssessment[goal.id] === 'high');
    
    if (highRiskGoals.length > 0) {
      recommendations.push(`Consider increasing contributions for ${highRiskGoals.length} high-risk goals`);
    }

    const debtGoals = incompleteGoals.filter(goal => goal.type === 'Debt');
    if (debtGoals.length > 0) {
      const highInterestDebt = debtGoals.filter(goal => (goal.apr || 0) > 15);
      if (highInterestDebt.length > 0) {
        recommendations.push('Prioritize high-interest debt payments to reduce total interest costs');
      }
    }

    const savingsGoals = incompleteGoals.filter(goal => goal.type === 'Savings');
    if (savingsGoals.length > 0) {
      const lowProgressSavings = savingsGoals.filter(goal => 
        (goal.currentAmount / goal.targetAmount) < 0.3
      );
      if (lowProgressSavings.length > 0) {
        recommendations.push('Consider increasing savings contributions for better progress');
      }
    }

    return {
      estimatedCompletionDates,
      goalAchievementProbability,
      riskAssessment,
      recommendations
    };
  }

  private static calculateMonthlyProgressTrend(goals: Goal[]): Array<{ month: string; progress: number }> {
    const trend: Array<{ month: string; progress: number }> = [];
    const now = new Date();
    
    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthKey = date.toISOString().slice(0, 7); // YYYY-MM format
      
      // Calculate average progress for goals active in this month
      const activeGoals = goals.filter(goal => {
        const goalDate = new Date(goal.createdAt || goal.lastUpdated);
        return goalDate <= date;
      });
      
      if (activeGoals.length > 0) {
        const avgProgress = activeGoals.reduce((sum, goal) => 
          sum + (goal.currentAmount / goal.targetAmount) * 100, 0
        ) / activeGoals.length;
        
        trend.push({
          month: monthKey,
          progress: Math.round(avgProgress * 10) / 10
        });
      }
    }
    
    return trend;
  }

  private static calculateMonthlyContributionTrend(goals: Goal[]): Array<{ month: string; amount: number }> {
    const trend: Array<{ month: string; amount: number }> = [];
    const now = new Date();
    
    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthKey = date.toISOString().slice(0, 7);
      
      // Sum monthly contributions for goals active in this month
      const activeGoals = goals.filter(goal => {
        const goalDate = new Date(goal.createdAt || goal.lastUpdated);
        return goalDate <= date;
      });
      
      const totalContribution = activeGoals.reduce((sum, goal) => 
        sum + goal.monthlyContribution, 0
      );
      
      trend.push({
        month: monthKey,
        amount: totalContribution
      });
    }
    
    return trend;
  }

  private static calculateMonthsToDeadline(deadline: string): number {
    const deadlineDate = new Date(deadline);
    const now = new Date();
    const diffTime = deadlineDate.getTime() - now.getTime();
    const diffMonths = Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 30.44)); // Average days per month
    return Math.max(diffMonths, 1);
  }
} 